import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Wallet, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { api } from "@/lib/api";
import { SectionHeader, StatTile, Button, Loading, EmptyState, Tag } from "@/components/brand";
import { formatR } from "@/lib/format";
import dayjs from "dayjs";

export default function TrainerEarnings() {
  const qc = useQueryClient();
  const e = useQuery({ queryKey: ["trainer", "earnings"], queryFn: () => api("/trainer/earnings") });
  const payout = useMutation({ mutationFn: () => api("/trainer/payouts", { method: "POST" }), onSuccess: () => { qc.invalidateQueries({ queryKey: ["trainer", "earnings"] }); toast.success("Payout requested"); }, onError: (err) => toast.error(err.message) });

  if (e.isLoading) return <Loading />;
  const d = e.data || {};
  const w = d.wallet || {};

  return (
    <div data-testid="trainer-earnings-screen" className="space-y-6">
      <SectionHeader title="Earnings" subtitle={`Platform commission: ${Math.round((d.commission_rate ?? 0.15) * 100)}%`} />
      <div className="p-5 rounded-xl2 bg-ink text-white flex flex-wrap items-center gap-4">
        <Wallet size={30} className="text-brand" />
        <div className="flex-1"><div className="font-body text-xs text-white/70">Available balance</div><div className="font-display text-3xl">{formatR(w.available)}</div></div>
        <Button testID="request-payout" title="Request Payout" disabled={(w.available ?? 0) <= 0} loading={payout.isPending} onClick={() => payout.mutate()} />
      </div>
      <div className="flex flex-wrap gap-3">
        <StatTile testID="stat-pending" label="Pending" value={formatR(w.pending)} />
        <StatTile testID="stat-earned" label="Total earned" value={formatR(w.total_earned)} />
        <StatTile testID="stat-paid" label="Paid out" value={formatR(w.paid_out)} />
      </div>

      <div>
        <h3 className="font-display text-xl text-ink mb-3">Transactions</h3>
        {(d.transactions ?? []).length === 0 ? <EmptyState title="No transactions yet" /> : (
          <div className="space-y-2">
            {d.transactions.map((t) => (
              <div key={t.id} className="flex items-center gap-3 p-3 rounded-md border border-divider">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center ${t.amount >= 0 ? "bg-brand-soft" : "bg-surface2"}`}>
                  {t.amount >= 0 ? <ArrowUpRight size={16} className="text-brand" /> : <ArrowDownRight size={16} className="text-mutedgrey" />}
                </div>
                <div className="flex-1 min-w-0"><div className="font-body font-semibold text-sm text-ink truncate">{t.title}</div><div className="font-body text-xs text-mutedgrey">{dayjs(t.created_at).format("D MMM, HH:mm")} · {t.type}</div></div>
                <span className={`font-display ${t.amount >= 0 ? "text-brand" : "text-mutedgrey"}`}>{t.amount >= 0 ? "+" : ""}{formatR(t.amount)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {(d.payouts ?? []).length > 0 ? (
        <div>
          <h3 className="font-display text-xl text-ink mb-3">Payouts</h3>
          <div className="space-y-2">
            {d.payouts.map((p) => (
              <div key={p.id} className="flex items-center gap-3 p-3 rounded-md border border-divider">
                <div className="flex-1"><div className="font-body font-semibold text-sm text-ink">{formatR(p.amount)}</div><div className="font-body text-xs text-mutedgrey">{dayjs(p.created_at).format("D MMM YYYY")}</div></div>
                <Tag label={p.status} tone={p.status === "paid" ? "green" : "grey"} />
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
